import React, { useState, useEffect } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import StyledButton from './ui/StyledButton';
import { X } from 'lucide-react';
import { handleFareHarborBooking } from '@/utils/fareHarborUtils';

interface StickyBookingButtonProps {
  link: string;
  itemId?: number; // Added itemId for direct booking
  flowId?: number; // Added flowId for direct booking
}

const StickyBookingButton = ({ 
  link,
  itemId,
  flowId = 1219724
}: StickyBookingButtonProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    // Check if previously dismissed
    const dismissed = localStorage.getItem('stickyButtonDismissed');
    if (dismissed && Date.now() - parseInt(dismissed) < 24 * 60 * 60 * 1000) {
      setIsDismissed(true);
    }
    
    const handleScroll = () => {
      // Show button after scrolling down 300px
      if (window.scrollY > 300) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const handleDismiss = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDismissed(true);
    
    // Remember dismissal for 24 hours
    localStorage.setItem('stickyButtonDismissed', Date.now().toString());
  };

  // Handle the click on the button
  const handleButtonClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // If we have an itemId, use FareHarbor direct booking
    if (itemId) {
      handleFareHarborBooking({
        e,
        itemId,
        flowId,
        lightframe: true
      });
      return;
    }
    
    // Otherwise, use the link as is
    if (link.startsWith('#')) {
      e.preventDefault();
      const element = document.querySelector(link);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  if (isDismissed) return null;

  return (
    <div 
      className={`fixed bottom-0 left-0 w-full p-4 bg-white/95 backdrop-blur-sm shadow-lg transform transition-transform duration-300 z-40 ${
        isVisible ? 'translate-y-0' : 'translate-y-full'
      }`}
    >
      <div className="container mx-auto flex items-center">
        <StyledButton
          href={link}
          variant="primary"
          fullWidth={true}
          showCalendarIcon={true}
          bookingButton={true}
          size="lg"
          className="font-montserrat font-semibold w-full"
          ariaLabel="Reserve your spot for a cat history tour"
          onClick={handleButtonClick}
        >
          Reserve Your Spot
        </StyledButton>
        
        <button 
          onClick={handleDismiss}
          className="ml-4 p-2 rounded-full hover:bg-gray-200 transition-colors"
          aria-label="Dismiss booking reminder"
        >
          <X size={20} className="text-gray-500" />
        </button>
      </div>
    </div>
  );
};

export default StickyBookingButton;
